import React, { useState, useEffect, useRef } from "react";
import { useTheme, styled } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useQuery } from "@tanstack/react-query";
import * as api from "../actions/api";

import "react-tabulator/lib/styles.css";
import "react-tabulator/lib/css/tabulator.min.css";
import { ReactTabulator } from "react-tabulator";
import { useTranslation } from "react-i18next";

const DispensingLanguage = JSON.parse(localStorage.getItem("DispensingLanguage"));
const lan = localStorage.getItem("LangCode");
const menukey = "entry-of-the-results-of-the-various-assessments-for-the-patients";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          className="float-end"
          size="large">
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

/**
 * Same layout/behavior as LabTestModal.js, but loads tests for the selected appointment profile
 * (getAppointmentLabTests). ItemGroupId is not required in `enabled` — edit payloads often omit it.
 */
const ProductsLotModal = ({
  handleClose,
  open,
  addProduct,
  FacilityId,
  TestToFacilityId,
  TestProfileGroupId,
  ...props
}) => {
  const { t } = useTranslation();

  const tblLotRefHolder = useRef({ table: null });
  const tblLotRef = tblLotRefHolder.current;
  const ItemGroupId = props.formData.ItemGroupId ?? "";

  const [tabledata, setTabledata] = useState([]);
  const { isLoading, error, isError, data, refetch } = useQuery(
    ["lab-test-list-appointment-entry", FacilityId, ItemGroupId, TestToFacilityId, TestProfileGroupId],
    () =>
      api
        .getAppointmentLabTestModalList(
          FacilityId,
          ItemGroupId,
          TestToFacilityId,
          TestProfileGroupId
        )
        .then((labtestlist) => {
          const list = Array.isArray(labtestlist) ? labtestlist : [];
          setTabledata(list);
          return list;
        }),
    {
      /* Backend getAppointmentLabTests only needs TestProfileGroupId. Do not require ItemGroupId or
         TestToFacilityId here — edit forms often omit ItemGroupId; old pasted code used Boolean(FacilityId,...) so
         only FacilityId effectively mattered. */
      enabled: Boolean(FacilityId && TestProfileGroupId),
    }
  );

  const ProductColumns = [
    { title: "TestId", field: "TestId", visible: false },
    { title: "TestProfileId", field: "TestProfileId", visible: false },
    {
      title: t(DispensingLanguage[lan][menukey]["Sl#"]),
      formatter: "rownum",
      width: 50,
      hozAlign: "center",
      headerHozAlign: "center",
      headerSort: false,
    },
    { title: t(DispensingLanguage[lan][menukey]["Test Name"]), field: "TestName", headerFilter: true },
    { title: t(DispensingLanguage[lan][menukey]["Test Unit"]), field: "UnitName", headerFilter: true, width: 100 },
  ];

  const ProdustDataItemsLot = tabledata?.map((item) => item);

  const [btnDisable, setbtnDisable] = useState(false);
  let selectedRows = [];

  const SaveSelectedProducts = () => {
    if (tblLotRef.table.getSelectedData().length > 0) {
      setbtnDisable(true);
      selectedRows = tblLotRef.table.getSelectedData();
      addProduct(tblLotRef.table.getSelectedData());
      setTimeout(() => setbtnDisable(false), 500);
    }
  };

  useEffect(() => {
    if (tblLotRef.table == null) return;
  }, []);

  return (
    <div>
      <BootstrapDialog
        onClose={() => handleClose("Products")}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth={true}
        maxWidth="xs">
        <BootstrapDialogTitle id="customized-dialog-title" onClose={() => handleClose("Products")}>
          {t(DispensingLanguage[lan][menukey]["Lab Test List"])}
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <ReactTabulator
            onRef={(rf) => {
              tblLotRef.table = rf.current;
            }}
            options={{
              selectableRowsRangeMode: "click",
              selectableRows: true,
            }}
            columns={ProductColumns}
            data={ProdustDataItemsLot}
            height="300px"
            layout={"fitColumns"}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={btnDisable}
            className="me-2 float-start"
            variant="contained"
            color="primary"
            onClick={SaveSelectedProducts}>
            {t(DispensingLanguage[lan][menukey]["ADD"])}
          </Button>
          <Button
            autoFocus
            className="float-start"
            variant="contained"
            color="gray"
            onClick={() => handleClose("Products")}>
            {t(DispensingLanguage[lan][menukey]["CANCEL"])}
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default ProductsLotModal;

const useStyles = makeStyles({
  fullWidth: {
    width: "100%",
  },
});
